/**
 * Plays the chat "new message" notification sound via the Web Audio API.
 *
 * Deliberately NOT an <audio> element: WKWebView registers HTMLMediaElement
 * playback with macOS's system media remote, which pops up a "Now Playing"
 * track with a play/pause control in Control Center and the Touch Bar for
 * every one-shot UI sound. AudioBufferSourceNode playback never creates a
 * media session, so nothing shows up there.
 *
 * The mp3 lives in `public/assets/sounds/` so CRA serves it from the site
 * root at `/assets/sounds/...`.
 */
const SOUND_URL = '/assets/sounds/new-message.mp3';
const VOLUME = 0.5;

let ctx = null;
let gainNode = null;
let bufferPromise = null;

function getContext() {
  if (!ctx) {
    const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
    ctx = new AudioContextCtor();
    gainNode = ctx.createGain();
    gainNode.gain.value = VOLUME;
    gainNode.connect(ctx.destination);
  }
  return ctx;
}

function getBuffer() {
  if (!bufferPromise) {
    const context = getContext();
    bufferPromise = fetch(SOUND_URL)
      .then((res) => res.arrayBuffer())
      .then((data) => context.decodeAudioData(data));
  }
  return bufferPromise;
}

// Browsers/WKWebView suspend a new AudioContext until a user gesture
// (autoplay policy), which would silently drop the very first notification
// after launch. Resume it and warm the decoded buffer on the earliest
// interaction.
let primed = false;
function prime() {
  if (primed) return;
  primed = true;
  window.removeEventListener('pointerdown', prime);
  window.removeEventListener('keydown', prime);
  try {
    const context = getContext();
    if (context.state === 'suspended') context.resume().catch(() => {});
    getBuffer().catch(() => {});
  } catch {
    /* ignore */
  }
}
if (typeof window !== 'undefined') {
  window.addEventListener('pointerdown', prime);
  window.addEventListener('keydown', prime);
}

export function playNewMessageSound() {
  try {
    const context = getContext();
    if (context.state === 'suspended') context.resume().catch(() => {});
    getBuffer()
      .then((buffer) => {
        const source = context.createBufferSource();
        source.buffer = buffer;
        source.connect(gainNode);
        source.start(0);
      })
      .catch(() => {});
  } catch {
    /* audio unavailable — ignore */
  }
}
